import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { FaProductHunt } from "react-icons/fa6";
import { useUtils } from "@/app/hooks/useUtils";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IPaymentModeChangeFilterData } from "@/api_framework/api_modals/payment";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { usePaymentDetailsAPI } from "@/app/hooks/api_hooks/payment/usePaymentDetailsAPI";
import { usePaymentMethodListAPI } from "@/app/hooks/api_hooks/payment/usePaymentMethodListAPI";
import { usePaymentModeChangeAPI } from "@/app/hooks/api_hooks/payment/usePaymentModeChangeAPI";

export interface IUpdatePaymentDetailsProps {
  payments: string;
}

const PaymentDetails = () => {
  const { getRouteKey } = useRouter();
  const [showResults, setShowResults] = useState<boolean>(false);
  const { execute: fetchPaymentMode, paymentMode } = usePaymentMethodListAPI();
  const paymentMethodItems = useSelectMenuReducer(paymentMode, "payment_mode", "payment_mode");
  const { execute: fetchPaymentDetails } = usePaymentDetailsAPI();
  const routeIdConfig = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  const { paymentDetails } = routeState.state as IInventoryState;
  const { veticDateFormat } = useUtils();
  const { execute: updatePaymentMode } = usePaymentModeChangeAPI();

  const formHook = useForm<IUpdatePaymentDetailsProps>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    fetchPaymentMode();
  }, []);

  useEffect(() => {
    if (paymentDetails && Object.keys(paymentDetails)?.length > 0 && formHook.watch("payments")?.length == 0) {
      formHook.setValue("payments", paymentDetails?.transaction_details?.transaction_mode);
    }
  }, [paymentDetails, paymentMethodItems]);

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setShowResults(false);
      return;
    }
    fetchPaymentDetails(query);
    setShowResults(true);
  };

  const handleSubmit = async (data: IUpdatePaymentDetailsProps) => {
    const constructedBody: IPaymentModeChangeFilterData = {
      payment_mode: data?.payments,
      transaction_id: paymentDetails?.transaction_details?.transaction_id,
    };
    try {
      await updatePaymentMode(constructedBody);
      formHook.reset();
      setShowResults(false);
      window.location.reload();
    } catch (error) {
      console.error("Failed to update payment mode:", error);
    }
  };

  return (
    <div className="bg-white px-4 p-4 rounded-md">
      <h2 className="flex justify-start items-center gap-x-3 border-gray-200 pb-3 border-b font-semibold text-sm">
        Update Payment Details
      </h2>
      <div className="flex gap-5 items-baseline mt-5">
        <div className="px-4 w-96">
          <InputWithAddOn
            onEnterKeyPress={(query: string) => {
              handleSearch(query);
            }}
            containerClassName="w-full"
            addon={<FaProductHunt className="w-4 h-4" />}
            placeholder="Search Payment ID"
          />
        </div>
      </div>
      {showResults && paymentDetails && (
        <form className="mt-5" onSubmit={formHook.handleSubmit(handleSubmit)}>
          <p className="flex justify-start items-center gap-x-3 font-medium text-sm mt-10">Client Details</p>
          <div className="flex justify-start items-baseline">
            <div className="flex justify-start items-center gap-1 p-2.5 w-64 h-[38px]">
              <span className="font-normal text-black text-xs">Client Name</span>
            </div>
            <div className="w-2/5">
              <p>{paymentDetails?.client_details?.client_name}</p>
            </div>
          </div>
          <div className="flex justify-start items-baseline">
            <div className="flex justify-start items-center gap-1 p-2.5 w-64 h-[38px]">
              <span className="font-normal text-black text-xs">Client Mobile Number</span>
            </div>
            <div className="w-2/5">
              <p>{paymentDetails?.client_details?.client_mobile_number}</p>
            </div>
          </div>
          <p className="flex justify-start items-center gap-x-3 font-medium text-sm mt-10">Payment Details</p>
          <div className="flex justify-start items-baseline">
            <div className="flex justify-start items-center gap-1 p-2.5 w-64 h-[38px]">
              <span className="font-normal text-black text-xs">Payment ID</span>
            </div>
            <div className="w-2/5">
              <p>{paymentDetails?.transaction_details?.transaction_id}</p>
            </div>
          </div>
          <div className="flex justify-start items-baseline">
            <div className="flex justify-start items-center gap-1 p-2.5 w-64 h-[38px]">
              <span className="font-normal text-black text-xs">Payment Date</span>
            </div>
            <div className="w-2/5">
              <p>{veticDateFormat(paymentDetails?.transaction_details?.transaction_date)}</p>
            </div>
          </div>
          <div className="flex justify-start items-baseline">
            <div className="flex justify-start items-center gap-1 p-2.5 w-64 h-[38px]">
              <span className="font-normal text-black text-xs">Amount</span>
            </div>
            <div className="w-2/5">
              <p>{paymentDetails?.transaction_details?.transaction_amount} /-</p>
            </div>
          </div>
          <div className="flex justify-start items-baseline">
            <div className="flex justify-start items-center gap-1 p-2.5 w-64 h-[38px]">
              <span className="font-normal text-black text-xs">Mode</span>
            </div>
            <div className="w-2/7">
              <SearchableSelectMenu
                errorMessages={[{ message: "Payment is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("payments", item.title);
                  }
                }}
                fieldError={formHook?.formState?.errors?.payments}
                register={formHook.register("payments", {
                  required: formHook.getValues("payments"),
                })}
                selectItems={paymentMethodItems}
                placeholder="Select Payment Mode"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={paymentMethodItems?.find((oc) => oc.title === formHook.watch("payments"))}
                listBoxClassName="w-full"
                className="text-gray-800 w-20"
                containerClassName="w-full"
              />
            </div>
          </div>
          <div className="right-10 bottom-5 fixed w-full flex justify-end items-center p-3 z-50">
            <input
              type="submit"
              value={"Save"}
              className="flex justify-center items-center gap-2 border-slate-400 bg-sky-900 px-10 py-[9px] border border-opacity-20 rounded-md h-9 font-medium text-white text-xs cursor-pointer"
              placeholder="Save"
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default PaymentDetails;
